import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { WhatsAppDeEnvio } from '@/modules/ordenes-servicio/WhatsAppDeEnvio'

import { ResultadoDelEnvio, type Envio } from './ResultadoDelEnvio'
import { enlaceDeCotizacion, useEnviarCotizacionAlCliente } from './useDecidirCotizacion'
import estilos from './DecidirCotizacion.module.css'

/**
 * AL ARMAR O ENLAZAR UNA COT: ¿SE LA MANDAMOS YA AL CLIENTE?
 *
 * Johan: «debería haber una forma de yo poder enviar por WhatsApp al cliente
 * una nueva COT cuando la armo o la enlazo (no FEV, porque FEV solo se hace
 * cuando él ya aceptó)… no tiene que ser obligatorio enviar al cliente».
 *
 * Es el mismo «Aprobar y enviar al cliente» de la ficha de la orden
 * (fn_enviar_cotizacion_al_cliente), ofrecido en el momento en que la
 * cotización nace, para no tener que ir a buscarla. No enviar no cambia
 * nada: la cotización queda en la orden y se envía después desde allá.
 *
 * Solo lo ve quien puede aprobar cotizaciones; los demás no ven nada.
 */
export function OfrecerEnvioAlCliente({
  cotizacionId,
  numero,
}: {
  cotizacionId: string
  numero?: string | null
}) {
  const { puede } = useSesion()
  const enviar = useEnviarCotizacionAlCliente()
  const [envio, setEnvio] = useState<Envio | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!puede('cotizaciones', 'aprobar')) return null
  if (envio) return <ResultadoDelEnvio envio={envio} />

  async function mandar() {
    setError(null)
    try {
      const r = await enviar.mutateAsync(cotizacionId)
      setEnvio({ enlace: enlaceDeCotizacion(r.token), salio: r.salio, motivo: r.motivo })
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo enviar al cliente.')
    }
  }

  return (
    <div className={estilos.ofrecer}>
      <p className={estilos.ofrecerTitulo}>¿Se la mandas ya al cliente?</p>
      <p className={estilos.ofrecerTexto}>
        Le llega {numero ? <b>{numero}</b> : 'la cotización'} por WhatsApp con un enlace para
        aceptarla. Es opcional: si no, queda en la orden y se envía cuando quieras desde allá.
      </p>
      {/* A dónde sale, antes de mandarla (0273). */}
      <WhatsAppDeEnvio cotizacionId={cotizacionId} compacto />
      {error && (
        <Aviso tipo="error" titulo="No se pudo enviar">
          {error}
        </Aviso>
      )}
      <div>
        <Boton variante="principal" onClick={() => void mandar()} trabajando={enviar.isPending}>
          Enviar al cliente por WhatsApp
        </Boton>
      </div>
    </div>
  )
}
